"use client";

import type { IExtProduceType } from "@agrigate/product-catalog-types";
import ComboboxSelect from "@agrigate/shared-web-ui/lib/ComboSelect";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import {
    Button,
    type Combobox,
    type ComboboxRootProps,
    Flex,
} from "@chakra-ui/react";
import type { CSSProperties, ElementType } from "react";
import { useMemo } from "react";
import { CiShare1 } from "react-icons/ci";
import { useProduceTypesQuery } from "../stores/produce-types";

export type PTItem = Pick<IExtProduceType, "id" | "name" | "key">;

export default function ProduceTypeDropdown(p: {
    name?: string;
    value?: MaybeN<string>;
    defaultValue?: MaybeN<string>;
    onChange?: (
        produceTypeId: MaybeN<string>,
        produceType: MaybeN<PTItem>,
    ) => void;
    autofetchInitial?: MaybeN<boolean>;
    items?: MaybeN<PTItem[]>;
    disabled?: boolean;
    style?: CSSProperties;
    width?: string;
    height?: string;
    customCaret?: ElementType;
    className?: string;
    onClean?: () => void;
    placement?: ComboboxRootProps["placeContent"];
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}) {
    const ptQH = useProduceTypesQuery({
        autoFetchInitial: p.autofetchInitial ?? !p.items,
        query: {
            enrichments: {
                varieties: true,
                categories: true,
            },
        },
    });

    const handleFetch = () => {
        if (p.items?.length || ptQH.entities?.length) return;
        ptQH.refetchInitial();
    };

    const sourceData = p.items ?? ptQH.entities ?? [];
    const options = useMemo(
        () =>
            sourceData.map(produceType => ({
                value: produceType.id,
                label: produceType.name ?? produceType.key,
            })),
        [sourceData],
    );

    const currentValue = p.value || p.defaultValue;

    return (
        <Flex
            direction="row"
            alignItems="center"
            flexGrow={1}
            style={p.style}
            width={p.width}
            height={p.height}
            gap={1}
        >
            <ComboboxSelect
                items={options}
                value={currentValue ? [currentValue] : []}
                placeholder="Select Produce Type"
                width="100%"
                comboRootProps={{
                    disabled: p.disabled,
                    className: p.className,
                }}
                onChange={selectedIds => {
                    const newId = selectedIds[0] || null;
                    if (!newId) {
                        p.onClean?.();
                        p.onChange?.(null, null);
                        return;
                    }
                    const fullItem = sourceData.find(i => i.id === newId);
                    p.onChange?.(newId, fullItem ?? null);
                }}
                onOpen={handleFetch}
                caretAs={p.customCaret}
                placement={p.placement}
                portalled={p.portalled ?? true}
                comboBoxProps={{ ...p.comboBoxProps }}
                comboInputProps={{ ...p.comboInputProps }}
            />

            {currentValue ? (
                <Button
                    size={"sm"}
                    variant={"ghost"}
                    flexShrink={0}
                    onClick={() => {
                        window.open(
                            `/produces-and-categories/hub?tab=produce-types&ptId=${currentValue}`,
                        );
                    }}
                >
                    <CiShare1 />
                </Button>
            ) : null}
        </Flex>
    );
}
